import React from "react";
import {useTranslation} from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";

import {Alert, Box, Button, Dialog, DialogContent, Tooltip, Typography,} from "@mui/material";

/**
 * SuccessDialog
 * @param {boolean} open
 * @param {function} onClose
 * @param {object} info
 * @param {string} [info.title]
 * @param {Array<{label: string, value: string, color?: string}>} [info.fields]
 * @param {string} [info.copyText]
 * @param {boolean} [info.showAlert]
 * @param {string} [info.alertText]
 */
export default function SuccessDialog({open, onClose, info = {}}) {
    const safeInfo = info || {};
    const {
        title = "",
        fields = [],
        copyText = "",
        showAlert = false,
        alertText = "",
    } = safeInfo;
    const {t} = useTranslation();
    const handleCopy = () => {
        if (copyText) navigator.clipboard.writeText(copyText);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                    overflow: "visible",
                },
            }}
        >
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    pt: 4,
                    pb: 3,
                    overflow: "visible",
                }}
            >

                <Box
                    sx={{
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        bgcolor: "success.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2.5,
                        boxShadow: "0px 8px 24px rgba(67, 160, 71, 0.4)",
                        animation: "successPulse 2s ease-in-out infinite",
                        "@keyframes successPulse": {
                            "0%, 100%": {
                                transform: "scale(1)",
                                boxShadow: "0px 8px 24px rgba(67, 160, 71, 0.4)",
                            },
                            "50%": {
                                transform: "scale(1.05)",
                                boxShadow: "0px 12px 32px rgba(67, 160, 71, 0.6)",
                            },
                        },
                    }}
                >
                    <CheckIcon sx={{fontSize: 50, color: "white", fontWeight: "bold"}}/>
                </Box>

                <Typography variant="h6" sx={{fontWeight: "bold", mb: 1}}>
                    {title}
                </Typography>

                {fields.length > 0 && (
                    <Box
                        sx={{
                            backgroundColor: "background.paper",
                            borderRadius: 2,
                            p: 2,
                            boxShadow: 1,
                            width: "100%",
                            mt: 1,
                        }}
                    >
                        {fields.map((f, i) => (
                            <Typography key={i} sx={{color: f.color || "inherit"}}>
                                <strong>{f.label}:</strong> {f.value}
                            </Typography>
                        ))}

                        {copyText && (
                            <Tooltip title={t("dialog.copyTooltip")}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{mt: 2}}
                                    onClick={handleCopy}
                                >
                                    {t("dialog.copyButton")}
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {showAlert && (
                    <Alert severity="info" sx={{mt: 3, fontSize: "0.9rem"}}>
                        {alertText}
                    </Alert>
                )}

                <Button
                    onClick={onClose}
                    variant="contained"
                    color="success"
                    sx={{
                        mt: 3,
                        px: 5,
                        py: 1.25,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        alignSelf: "center",
                        bgcolor: "success.main",
                        boxShadow: "0 4px 15px rgba(67, 160, 71, 0.4)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            bgcolor: "success.dark",
                            boxShadow: "0 6px 20px rgba(67, 160, 71, 0.6)",
                            transform: "translateY(-2px)",
                        },
                    }}
                >
                    {t("common.ok")}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
